import { z } from 'zod';
import { MoneySchema, TimestampFieldsSchema } from './common';

// ─── Resource Type ─────────────────────────────────────────────────────────
// Generic — รองรับทุกประเภทการจอง ไม่ใช่แค่ Room

export const ResourceTypeSchema = z.enum([
  'room',          // โรงแรม — ห้องพัก
  'table',         // ร้านอาหาร — โต๊ะ
  'appointment',   // คลินิก — นัดหมาย
  'course',        // สถาบัน — คอร์สเรียน
  'service',       // ฟิตเนส/สปา — บริการ
  'facility',      // สนามกีฬา, ห้องประชุม — สถานที่
  'product',       // ร้านค้า — สินค้า (pre-order)
  'custom',        // กำหนดเอง
]);
export type ResourceType = z.infer<typeof ResourceTypeSchema>;

// ─── Booking Status ────────────────────────────────────────────────────────

export const BookingStatusSchema = z.enum([
  'draft',         // กำลังสร้าง
  'pending',       // รอยืนยัน
  'confirmed',     // ยืนยันแล้ว
  'in_progress',   // กำลังดำเนินการ (checked-in, session started)
  'completed',     // เสร็จสิ้น
  'cancelled',     // ยกเลิก
  'no_show',       // ไม่มาตามนัด
]);
export type BookingStatus = z.infer<typeof BookingStatusSchema>;

// ─── Payment Status ────────────────────────────────────────────────────────

export const PaymentStatusSchema = z.enum([
  'pending',
  'authorized',
  'paid',
  'pay_at_hotel',
  'refunded',
  'failed',
  'waived',
]);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

// ─── Source ───────────────────────────────────────────────────────────────
// Channel ที่ทำ booking — ไม่ผูกกับ LINE เท่านั้นแล้ว

export const BookingSourceSchema = z.enum([
  'line',          // ผ่าน LINE OA / LINE Mini App
  'whatsapp',      // ผ่าน WhatsApp
  'wechat',        // ผ่าน WeChat
  'web',           // ผ่านเว็บไซต์
  'facebook',      // ผ่าน Facebook Messenger
  'admin',         // Staff ทำให้ใน Admin Dashboard
  'api',           // ผ่าน API โดยตรง
  'phone',         // Staff รับโทรศัพท์แล้วบันทึก
  'walk_in',       // Walk-in (ไม่ได้จองล่วงหน้า)
  'other',
]);
export type BookingSource = z.infer<typeof BookingSourceSchema>;

// ─── Generic Booking ──────────────────────────────────────────────────────
// ใช้ได้กับทุกธุรกิจ — hotel, clinic, restaurant, school, etc.

export const BookingSchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    contactId: z.string(),              // อ้างอิงไปที่ Contact (ไม่ใช่ guestId)

    // Resource ที่จอง
    resourceType: ResourceTypeSchema,
    resourceId: z.string().optional(),  // เช่น roomTypeCode, doctorId, tableId
    resourceName: z.string().optional(),

    // เวลา
    startAt: z.string().datetime({ offset: true }),
    endAt: z.string().datetime({ offset: true }).optional(),
    allDay: z.boolean().default(false),

    // Status
    status: BookingStatusSchema.default('pending'),
    paymentStatus: PaymentStatusSchema.default('pending'),

    // Financial
    totalAmount: MoneySchema.optional(),
    paidAmount: MoneySchema.optional(),

    // Source
    source: BookingSourceSchema,
    channelContactId: z.string().optional(), // เช่น LINE userId, WhatsApp phone

    // Notes
    notes: z.string().optional(),
    internalNotes: z.string().optional(),
    cancellationReason: z.string().optional(),

    // Flexible industry-specific data
    // Hotel: { checkIn, checkOut, nights, roomStays: [...] }
    // Clinic: { doctorId, treatmentCode, chiefComplaint }
    // Restaurant: { partySize, seatingArea, mealPeriod }
    customFields: z.record(z.string(), z.unknown()).default({}),

    // Timestamps
    confirmedAt: z.string().datetime({ offset: true }).optional(),
    cancelledAt: z.string().datetime({ offset: true }).optional(),
    completedAt: z.string().datetime({ offset: true }).optional(),
  })
  .merge(TimestampFieldsSchema);

export type Booking = z.infer<typeof BookingSchema>;

export const CreateBookingInputSchema = BookingSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  confirmedAt: true,
  cancelledAt: true,
  completedAt: true,
});
export type CreateBookingInput = z.infer<typeof CreateBookingInputSchema>;
